import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Play, 
  Pause,
  Calendar, 
  BarChart3, 
  Users, 
  Sparkles, 
  Link2, 
  ChevronRight,
  Clock,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const DEMO_VIDEOS = [
  {
    id: 1,
    title: "Platform Overview",
    description: "A complete walkthrough of StelliSocial's main features and interface",
    duration: "0:04",
    videoSrc: "/videos/demo-video-1.mp4",
    thumbnail: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=640&h=360&fit=crop",
    icon: Play,
    chapters: [
      { time: "0:00", title: "Introduction" },
      { time: "0:01", title: "Dashboard Overview" },
      { time: "0:02", title: "Navigation & Layout" },
      { time: "0:03", title: "Quick Actions" }
    ]
  },
  {
    id: 2,
    title: "Content Calendar & Scheduling",
    description: "Learn how to plan, schedule, and manage posts across all your social platforms",
    duration: "0:04",
    videoSrc: "/videos/demo-calendar.mp4",
    thumbnail: "https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=640&h=360&fit=crop",
    icon: Calendar,
    chapters: [
      { time: "0:00", title: "Calendar Views" },
      { time: "0:01", title: "Creating Posts" },
      { time: "0:02", title: "Drag & Drop Scheduling" },
      { time: "0:03", title: "Bulk Scheduling" }
    ]
  },
  {
    id: 3,
    title: "Analytics & Insights",
    description: "Track engagement, audience growth, and optimize your content strategy",
    duration: "0:04",
    videoSrc: "/videos/demo-video-2.mp4",
    thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=640&h=360&fit=crop",
    icon: BarChart3,
    chapters: [
      { time: "0:00", title: "Analytics Dashboard" },
      { time: "0:01", title: "Post Performance" },
      { time: "0:02", title: "Audience Insights" },
      { time: "0:03", title: "Custom Reports" }
    ]
  },
  {
    id: 4,
    title: "AI Content Generation",
    description: "Use AI to generate captions, hashtags, and even videos for your posts",
    duration: "0:04",
    videoSrc: "/videos/demo-video-3.mp4",
    thumbnail: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=640&h=360&fit=crop",
    icon: Sparkles,
    chapters: [
      { time: "0:00", title: "AI Caption Writer" },
      { time: "0:01", title: "Hashtag Generator" },
      { time: "0:02", title: "Content Ideas" },
      { time: "0:03", title: "Video Generation" }
    ]
  },
  {
    id: 5,
    title: "Connecting Social Accounts",
    description: "Step-by-step guide to connect all your social media platforms",
    duration: "0:04",
    videoSrc: "/videos/demo-video-4.mp4",
    thumbnail: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=640&h=360&fit=crop",
    icon: Link2,
    chapters: [
      { time: "0:00", title: "Supported Platforms" },
      { time: "0:01", title: "OAuth Connection" },
      { time: "0:02", title: "Managing Accounts" },
      { time: "0:03", title: "Troubleshooting" }
    ]
  },
  {
    id: 6,
    title: "Team Collaboration",
    description: "Set up your team, assign roles, and use approval workflows",
    duration: "0:04",
    videoSrc: "/videos/demo-video-5.mp4",
    thumbnail: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=640&h=360&fit=crop",
    icon: Users,
    chapters: [
      { time: "0:00", title: "Inviting Team Members" },
      { time: "0:01", title: "Role Permissions" },
      { time: "0:02", title: "Approval Workflows" },
      { time: "0:03", title: "Comments & Feedback" }
    ]
  }
];

const DemoVideoPage = () => {
  const [activeVideo, setActiveVideo] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);

  const currentVideo = DEMO_VIDEOS[activeVideo];

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    // Auto-advance to next video
    if (activeVideo < DEMO_VIDEOS.length - 1) {
      setTimeout(() => {
        setActiveVideo(activeVideo + 1);
      }, 1000);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  const handleNextVideo = () => {
    setActiveVideo((prev) => (prev + 1) % DEMO_VIDEOS.length);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handlePrevVideo = () => {
    setActiveVideo((prev) => (prev - 1 + DEMO_VIDEOS.length) % DEMO_VIDEOS.length);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <h1 className="text-xl font-bold text-white">Stelli<span className="text-emerald-400">Social</span> Demo</h1>
            </div>
          </div>
          <Link to="/register">
            <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">
            Product Demo
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            See StelliSocial in Action
          </h2>
          <p className="text-emerald-200 text-lg">
            Watch how easy it is to manage your social media presence
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <div className="relative mb-4">
              <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-white/10 relative group">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  onEnded={handleVideoEnd}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onError={(e) => console.log('Video error:', e)}
                  muted={isMuted}
                  playsInline
                  poster={currentVideo.thumbnail}
                  key={currentVideo.id}
                >
                  <source src={currentVideo.videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Play/Pause Overlay */}
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer"
                    onClick={handlePlayPause}
                  >
                    <button className="w-20 h-20 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-transform hover:scale-110">
                      <Play className="h-8 w-8 text-white ml-1" fill="white" />
                    </button>
                  </div>
                )}

                {/* Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Progress Bar */}
                  <div 
                    className="w-full h-1 bg-white/30 rounded-full mb-3 cursor-pointer"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full bg-emerald-500 rounded-full relative"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full" />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={handlePlayPause}
                        className="text-white hover:text-emerald-400 transition-colors"
                      >
                        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                      </button>
                      <button 
                        onClick={handlePrevVideo}
                        className="text-white hover:text-emerald-400 transition-colors"
                      >
                        <SkipBack className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={handleNextVideo}
                        className="text-white hover:text-emerald-400 transition-colors"
                      >
                        <SkipForward className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => setIsMuted(!isMuted)}
                        className="text-white hover:text-emerald-400 transition-colors"
                      >
                        {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                      </button>
                      <span className="text-white text-sm">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                    <button 
                      onClick={handleFullscreen}
                      className="text-white hover:text-emerald-400 transition-colors"
                    >
                      <Maximize className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Video Navigation Arrows */}
              <div className="absolute top-1/2 -translate-y-1/2 left-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrevVideo}
                  className="bg-black/50 hover:bg-black/70 text-white rounded-full h-10 w-10"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 right-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNextVideo}
                  className="bg-black/50 hover:bg-black/70 text-white rounded-full h-10 w-10"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Video Info */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {currentVideo.title}
                  </h3>
                  <p className="text-slate-400">
                    {currentVideo.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Clock className="h-4 w-4" />
                  {currentVideo.duration}
                </div>
              </div>
              
              {/* Chapters */}
              <div className="border-t border-white/10 pt-4 mt-4">
                <h4 className="text-sm font-medium text-slate-300 mb-3">Chapters</h4>
                <div className="grid grid-cols-2 gap-2">
                  {currentVideo.chapters.map((chapter, idx) => (
                    <button
                      key={idx}
                      className="flex items-center gap-2 text-left p-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <span className="text-emerald-400 text-xs font-mono">{chapter.time}</span>
                      <span className="text-slate-300 text-sm">{chapter.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Video List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white mb-4">All Tutorials</h3>
            {DEMO_VIDEOS.map((video, index) => {
              const Icon = video.icon;
              return (
                <button
                  key={video.id}
                  onClick={() => {
                    setActiveVideo(index);
                    setIsPlaying(false);
                    setCurrentTime(0);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${
                    activeVideo === index
                      ? 'bg-emerald-500/20 border-emerald-500'
                      : 'bg-white/5 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-28 h-16 object-cover rounded-lg"
                    />
                    <div className="absolute bottom-1 right-1 bg-black/70 px-1 py-0.5 rounded text-white text-xs">
                      {video.duration}
                    </div>
                    {activeVideo === index && (
                      <div className="absolute inset-0 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <Play className="h-6 w-6 text-white" fill="white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${
                      activeVideo === index ? 'text-emerald-400' : 'text-white'
                    }`}>
                      {video.title}
                    </p>
                    <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                      {video.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 mb-12">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <Calendar className="h-10 w-10 text-emerald-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Smart Scheduling</h3>
            <p className="text-slate-400 text-sm">Schedule posts across 11+ platforms with drag-and-drop calendar</p>
          </div>
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <BarChart3 className="h-10 w-10 text-emerald-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Analytics Dashboard</h3>
            <p className="text-slate-400 text-sm">Track engagement, audience growth, and best posting times</p>
          </div>
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <Users className="h-10 w-10 text-emerald-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Team Collaboration</h3>
            <p className="text-slate-400 text-sm">Multi-user roles, approval workflows, and commenting</p>
          </div>
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <Sparkles className="h-10 w-10 text-emerald-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">AI Content Ideas</h3>
            <p className="text-slate-400 text-sm">Generate post ideas and optimize content with AI</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl p-8 border border-emerald-500/30">
          <h3 className="text-2xl font-bold text-white mb-2">Ready to Get Started?</h3>
          <p className="text-slate-300 mb-6">Join thousands of marketers already using StelliSocial</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                View Pricing
              </Button>
            </Link>
          </div>
          <p className="text-slate-400 text-sm mt-4">No credit card required • 14-day free trial</p>
        </div>
      </main>
    </div>
  );
};

export default DemoVideoPage;
